import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OfferLetterTemplateBuilder.css';

const TemplateBuilderPage = ({ API_BASE_URL }) => {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [template, setTemplate] = useState({
    templateName: '',
    description: '',
    sections: [],
    defaultCompanyInfo: {},
    defaultPolicies: {},
    styling: {}
  });

  const [activeSectionId, setActiveSectionId] = useState(null);
  const [predefinedTemplates, setPredefinedTemplates] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);

  const [availableSections] = useState([
    {
      id: 'header',
      type: 'header',
      title: 'Letter Header',
      fields: [
        { 
          name: 'companyLogo', 
          label: 'Company Logo', 
          type: 'image', 
          required: false, 
          placeholder: 'Upload company logo',
          defaultValue: ''
        },
        { 
          name: 'companyName', 
          label: 'Company Name', 
          type: 'text', 
          required: true, 
          placeholder: 'Enter company name',
          defaultValue: ''
        },
        { 
          name: 'letterTitle', 
          label: 'Letter Title', 
          type: 'text', 
          required: true, 
          placeholder: 'Enter letter title',
          defaultValue: 'APPOINTMENT LETTER'
        }
      ]
    },
    {
      id: 'company_info',
      type: 'company_info',
      title: 'Company Information',
      fields: [
        { 
          name: 'companyAddress', 
          label: 'Company Address', 
          type: 'textarea', 
          required: true, 
          placeholder: 'Enter company address',
          defaultValue: ''
        },
        { 
          name: 'companyEmail', 
          label: 'Company Email', 
          type: 'email', 
          required: true, 
          placeholder: 'Enter company email',
          defaultValue: ''
        }
      ]
    },
    {
      id: 'candidate_info',
      type: 'candidate_info',
      title: 'Candidate Information',
      fields: [
        { 
          name: 'candidateName', 
          label: 'Candidate Name', 
          type: 'text', 
          required: true, 
          placeholder: 'Enter candidate name',
          defaultValue: ''
        },
        { 
          name: 'candidateEmail', 
          label: 'Candidate Email', 
          type: 'email', 
          required: true, 
          placeholder: 'Enter candidate email',
          defaultValue: ''
        }
      ]
    },
    {
      id: 'offer_details',
      type: 'offer_details',
      title: 'Offer Details',
      fields: [
        { 
          name: 'position', 
          label: 'Position', 
          type: 'text', 
          required: true, 
          placeholder: 'Enter position',
          defaultValue: ''
        },
        { 
          name: 'joiningDate', 
          label: 'Joining Date', 
          type: 'datepicker', 
          required: true, 
          placeholder: 'Select joining date',
          defaultValue: ''
        }
      ]
    },
    {
      id: 'signature',
      type: 'signature',
      title: 'Signatures',
      fields: [
        { 
          name: 'hrSignature', 
          label: 'HR Signature', 
          type: 'signature', 
          required: true, 
          placeholder: 'HR signature',
          defaultValue: ''
        }
      ]
    }
  ]);

  /* ================= DERIVED ================= */
  const activeSection = template.sections.find(
    section => section.id === activeSectionId
  );

  /* ================= EFFECT ================= */
  useEffect(() => {
    loadCompanyTemplates();
    getCompanyProfile();
  }, []);

  /* ================= API CALLS ================= */
  const getCompanyProfile = async () => {
    try {
      console.log('🔄 Fetching company profile...');
      setLoadingCompany(true);
      
      const res = await fetch(`${API_BASE_URL}/api/company/profile`, {
        credentials: 'include'
      });
      
      console.log('📊 Company profile response status:', res.status);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log('📋 Company profile data:', data);
      
      if (data?.success && data.data) {
        // Try different possible ID fields
        const companyId = data.data._id || data.data.id || data.data.companyId;
        const companyName = data.data.companyName || data.data.name || data.data.company_name || 'Unknown Company';
        
        console.log('✅ Company ID found:', companyId);
        console.log('✅ Company Name:', companyName);
        
        setCompanyProfile(data.data);
        setCompanyId(companyId);
      } else {
        console.error('❌ Failed to get company profile:', data?.message || 'No data returned');
        alert('Unable to load company information. Please login again.');
      }
    } catch (err) {
      console.error('❌ Error fetching company profile:', err);
      console.log('🔄 Trying alternative API endpoint...');
      
      // Try alternative endpoint
      try {
        const altRes = await fetch(`${API_BASE_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (altRes.ok) {
          const altData = await altRes.json();
          console.log('📋 Alternative profile data:', altData);
          
          if (altData?.data) {
            const companyId = altData.data.companyId || altData.data._id;
            if (companyId) {
              console.log('✅ Got company ID from alternative endpoint:', companyId);
              setCompanyId(companyId);
              setCompanyProfile(altData.data);
            }
          }
        }
      } catch (altErr) {
        console.error('❌ Alternative endpoint also failed:', altErr);
      }
    } finally {
      setLoadingCompany(false);
    }
  };

  const loadCompanyTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/offer-templates/company`, {
        credentials: 'include'
      });
      const data = await res.json();
      console.log('Templates Response:', data);
      
      if (data.success) {
        setPredefinedTemplates(data.data);
      } else {
        console.warn('No templates found or error:', data.message);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  /* ================= TEMPLATE ACTIONS ================= */
  const addSection = (section) => {
    const newSection = {
      ...JSON.parse(JSON.stringify(section)),
      id: `${section.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      order: template.sections.length
    };
    
    // Auto-populate company info if adding company_info section and we have company profile
    if (section.type === 'company_info' && companyProfile) {
      newSection.fields = newSection.fields.map(field => {
        if (field.name === 'companyName' && companyProfile.companyName) {
          return { ...field, defaultValue: companyProfile.companyName };
        }
        if (field.name === 'companyAddress' && companyProfile.address) {
          return { ...field, defaultValue: companyProfile.address };
        }
        if (field.name === 'companyEmail' && companyProfile.email) {
          return { ...field, defaultValue: companyProfile.email };
        }
        return field;
      });
    }
    
    setTemplate(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const updateSection = (sectionId, callback) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId ? callback(section) : section
      )
    }));
  };

  const removeSection = (id) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.id !== id)
    }));
    setActiveSectionId(null);
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    setTemplate(prev => {
      const newSections = [...prev.sections];
      const temp = newSections[index];
      newSections[index] = newSections[index - 1];
      newSections[index - 1] = temp;
      
      // Update order numbers
      return { 
        ...prev, 
        sections: newSections.map((section, idx) => ({
          ...section,
          order: idx
        }))
      };
    });
  };

  const moveSectionDown = (index) => {
    if (index === template.sections.length - 1) return;
    setTemplate(prev => {
      const newSections = [...prev.sections];
      const temp = newSections[index];
      newSections[index] = newSections[index + 1];
      newSections[index + 1] = temp;
      
      // Update order numbers
      return { 
        ...prev, 
        sections: newSections.map((section, idx) => ({
          ...section,
          order: idx
        }))
      };
    });
  };

  const addCustomField = (sectionId) => {
    const fieldName = prompt('Enter field name (use camelCase, e.g., employeeId):');
    if (!fieldName) return;

    // Validate field name
    if (!/^[a-z][a-zA-Z0-9]*$/.test(fieldName)) {
      alert('Field name must start with lowercase letter and contain only letters and numbers');
      return;
    }

    const fieldLabel = fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());

    updateSection(sectionId, section => ({
      ...section,
      fields: [
        ...section.fields,
        {
          name: fieldName,
          label: fieldLabel,
          type: 'text',
          required: false,
          placeholder: `Enter ${fieldLabel.toLowerCase()}`,
          defaultValue: ''
        }
      ]
    }));
  };

  const updateFieldProperty = (sectionId, fieldName, property, value) => {
    updateSection(sectionId, section => ({
      ...section,
      fields: section.fields.map(field =>
        field.name === fieldName
          ? { ...field, [property]: value }
          : field
      )
    }));
  };

  const removeField = (sectionId, fieldName) => {
    if (window.confirm('Remove this field?')) {
      updateSection(sectionId, section => ({
        ...section,
        fields: section.fields.filter(field => field.name !== fieldName)
      }));
    }
  };

  /* ================= IMAGE UPLOAD HANDLER ================= */
  const handleImageUpload = (sectionId, fieldName, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, SVG)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      updateFieldProperty(sectionId, fieldName, 'defaultValue', base64Image);
      updateFieldProperty(sectionId, fieldName, 'fileName', file.name);
    };
    reader.readAsDataURL(file);
  };

  /* ================= FIXED: SAVE TEMPLATE ================= */
  const saveTemplate = async () => {
    if (!template.templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (template.sections.length === 0) {
      alert('Please add at least one section to the template');
      return;
    }

    if (!companyId) {
      alert('Company information not found. Please make sure you are logged in.');
      return;
    }

    setIsSaving(true);

    try {
      // Prepare template data exactly as backend expects
      const templateData = {
        templateName: template.templateName.trim(),
        description: template.description.trim() || '',
        sections: template.sections.map(section => ({
          type: section.type,
          title: section.title,
          order: section.order || 0,
          fields: section.fields.map(field => ({
            name: field.name,
            label: field.label,
            type: field.type === 'datepicker' ? 'datepicker' : field.type, // Ensure datepicker type
            required: field.required || false,
            placeholder: field.placeholder || '',
            defaultValue: field.defaultValue || ''
          }))
        })),
        defaultCompanyInfo: template.defaultCompanyInfo,
        defaultPolicies: template.defaultPolicies,
        styling: template.styling,
        companyId: companyId,
        status: 'active',
        isDefault: false
      };

      console.log('Saving template to database:', templateData);

      const res = await fetch(`${API_BASE_URL}/api/offer-templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(templateData)
      });

      // Try to parse response as JSON
      let data;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Server response error');
      }

      console.log('Save response:', data);

      if (res.ok && data.success) {
        alert('✅ Template saved successfully to database!');
        
        // Reset form
        setTemplate({
          templateName: '',
          description: '',
          sections: [],
          defaultCompanyInfo: {},
          defaultPolicies: {},
          styling: {}
        });
        setActiveSectionId(null);
        
        // Reload templates list
        loadCompanyTemplates();
      } else {
        console.error('Save failed:', data);
        alert(`Error saving template: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Network error saving template:', err);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const loadTemplate = (selectedTemplate) => {
    if (window.confirm('Load this template? Current work will be lost.')) {
      const clonedTemplate = JSON.parse(JSON.stringify(selectedTemplate));
      setTemplate({
        templateName: clonedTemplate.templateName || '',
        description: clonedTemplate.description || '',
        sections: clonedTemplate.sections || [],
        defaultCompanyInfo: clonedTemplate.defaultCompanyInfo || {},
        defaultPolicies: clonedTemplate.defaultPolicies || {},
        styling: clonedTemplate.styling || {}
      });
      setActiveSectionId(null);
      alert('Template loaded successfully!');
    }
  };

  /* ================= DATE PICKER PREVIEW ================= */
  const DatePickerPreview = ({ value, placeholder }) => {
    const formattedDate = value ? new Date(value).toLocaleDateString() : '';
    
    return (
      <div className="datepicker-preview">
        <input 
          type="text" 
          placeholder={placeholder || 'Select date'} 
          value={formattedDate}
          disabled 
          className="date-input"
        />
        <span className="datepicker-icon">📅</span>
      </div>
    );
  };

  /* ================= MANUAL COMPANY ID INPUT ================= */
  const handleManualCompanyId = () => {
    const manualId = prompt('Enter Company ID manually:');
    if (manualId && manualId.trim()) {
      setCompanyId(manualId.trim());
      alert('Company ID set manually. You can now save templates.');
    }
  };

  /* ================= UI ================= */
  return (
    <div className="template-builder-page">

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1>📝 Offer Letter Template Builder</h1>
        <p>Create reusable templates for offer letters</p>
        
        <div className="company-status">
          {loadingCompany ? (
            <div className="company-loading">Loading company info...</div>
          ) : companyId ? (
            <div className="company-info-badge">
              ✅ Company ID: {companyId.substring(0, 8)}...
              {companyProfile?.companyName && ` | Company: ${companyProfile.companyName}`}
            </div>
          ) : (
            <div className="company-warning">
              ⚠️ Company not detected. 
              <button 
                className="manual-id-btn"
                onClick={handleManualCompanyId}
              >
                Enter Company ID Manually
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="builder-container">

        {/* LEFT PANEL - Available Sections */}
        <div className="sections-panel">
          <h3>📦 Available Sections</h3>
          <p>Click to add to template</p>
          
          <div className="available-sections-list">
            {availableSections.map(section => (
              <div
                key={section.id}
                className="section-item"
                onClick={() => addSection(section)}
              >
                <div className="section-icon">
                  {section.type === 'header' && '🏢'}
                  {section.type === 'company_info' && 'ℹ️'}
                  {section.type === 'candidate_info' && '👤'}
                  {section.type === 'offer_details' && '💼'}
                  {section.type === 'signature' && '✍️'}
                </div>
                <div className="section-info">
                  <strong>{section.title}</strong>
                  <small>{section.fields?.length} fields</small>
                </div>
              </div>
            ))}
          </div>

          {/* Saved Templates */}
          {predefinedTemplates.length > 0 && (
            <div className="saved-templates">
              <h4>💾 Saved Templates</h4>
              {predefinedTemplates.map(tpl => (
                <div 
                  key={tpl._id} 
                  className="saved-template"
                  onClick={() => loadTemplate(tpl)}
                >
                  <strong>{tpl.templateName}</strong>
                  <small>{tpl.sections?.length} sections</small>
                  <small>{tpl.createdAt ? new Date(tpl.createdAt).toLocaleDateString() : 'Unknown date'}</small>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CENTER PANEL - Template Editor */}
        <div className="template-editor">
          <div className="editor-header">
            <div className="template-name-group">
              <input
                type="text"
                placeholder="Enter Template Name (e.g., Standard Offer Letter)"
                value={template.templateName}
                onChange={(e) =>
                  setTemplate(prev => ({ ...prev, templateName: e.target.value }))
                }
                className="template-name-input"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={template.description}
                onChange={(e) =>
                  setTemplate(prev => ({ ...prev, description: e.target.value }))
                }
                className="template-desc-input"
              />
            </div>
            <div className="save-section">
              <button 
                onClick={saveTemplate} 
                className="save-btn"
                disabled={isSaving || !companyId}
                title={!companyId ? "Company ID required to save" : "Save template"}
              >
                {isSaving ? 'Saving...' : '💾 Save Template'}
              </button>
              {!companyId && (
                <div className="save-hint">
                  ⚠️ Cannot save without Company ID
                </div>
              )}
            </div>
          </div>

          {template.sections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>No Sections Added</h3>
              <p>Click sections from the left to build your template</p>
              <p className="hint">Start with "Letter Header"</p>
            </div>
          ) : (
            <div className="sections-list">
              {template.sections
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((section, index) => (
                <div
                  key={section.id}
                  className={`template-section ${
                    section.id === activeSectionId ? 'active' : ''
                  }`}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  <div className="section-header">
                    <div className="section-title">
                      <span className="section-number">{index + 1}</span>
                      <strong>{section.title}</strong>
                    </div>
                    <div className="section-controls">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionUp(index);
                        }}
                        disabled={index === 0}
                        title="Move Up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionDown(index);
                        }}
                        disabled={index === template.sections.length - 1}
                        title="Move Down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSection(section.id);
                        }}
                        title="Remove Section"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="section-fields">
                    {section.fields?.map(field => (
                      <div key={field.name} className="field-preview">
                        <label>
                          {field.label}
                          {field.required && <span className="required-star"> *</span>}
                        </label>
                        
                        {field.type === 'textarea' ? (
                          <textarea 
                            placeholder={field.placeholder || ''} 
                            disabled 
                            rows="2" 
                            value={field.defaultValue || ''}
                          />
                        ) : field.type === 'datepicker' || field.type === 'date' ? (
                          <DatePickerPreview 
                            value={field.defaultValue} 
                            placeholder={field.placeholder} 
                          />
                        ) : field.type === 'signature' ? (
                          <div className="signature-box">
                            {field.defaultValue ? '[Signature Added]' : '[Signature]'}
                          </div>
                        ) : field.type === 'image' ? (
                          <div className="image-upload-box">
                            {field.defaultValue ? (
                              <div className="image-preview">
                                <img src={field.defaultValue} alt="Preview" />
                                <small>{field.fileName || 'Uploaded Image'}</small>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(section.id, field.name, e)}
                                  onClick={(e) => e.stopPropagation()}
                                  id={`image-upload-${section.id}-${field.name}`}
                                  style={{ display: 'none' }}
                                />
                                <label 
                                  htmlFor={`image-upload-${section.id}-${field.name}`}
                                  className="image-upload-label"
                                >
                                  📁 Upload Image
                                </label>
                                <small>Click to upload logo (max 5MB)</small>
                              </>
                            )}
                          </div>
                        ) : field.type === 'email' ? (
                          <input 
                            type="email" 
                            placeholder={field.placeholder || ''} 
                            disabled 
                            value={field.defaultValue || ''}
                          />
                        ) : (
                          <input 
                            type="text" 
                            placeholder={field.placeholder || ''} 
                            disabled 
                            value={field.defaultValue || ''}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT PANEL - Section Editor */}
        {activeSection && (
          <div className="section-editor">
            <h3>Edit Section: {activeSection.title}</h3>
            
            <div className="editor-form">
              <div className="form-group">
                <label>Section Title</label>
                <input
                  type="text"
                  value={activeSection.title}
                  onChange={(e) =>
                    updateSection(activeSection.id, s => ({
                      ...s,
                      title: e.target.value
                    }))
                  }
                  placeholder="Enter section title"
                />
              </div>

              <h4>Fields ({activeSection.fields?.length || 0})</h4>
              
              {activeSection.fields?.length > 0 ? (
                activeSection.fields.map(field => (
                  <div key={field.name} className="field-editor">
                    <div className="field-header">
                      <strong>{field.label}</strong>
                      <button
                        className="remove-field-btn"
                        onClick={() => removeField(activeSection.id, field.name)}
                        title="Remove Field"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="field-properties">
                      <div className="property-row">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) =>
                              updateFieldProperty(
                                activeSection.id,
                                field.name,
                                'required',
                                e.target.checked
                              )
                            }
                          />
                          <span>Required Field</span>
                        </label>
                      </div>
                      
                      <div className="property-row">
                        <label>Field Type:</label>
                        <select
                          value={field.type || 'text'}
                          onChange={(e) =>
                            updateFieldProperty(
                              activeSection.id,
                              field.name,
                              'type',
                              e.target.value
                            )
                          }
                        >
                          <option value="text">Text</option>
                          <option value="textarea">Text Area</option>
                          <option value="datepicker">Date Picker</option>
                          <option value="email">Email</option>
                          <option value="image">Image</option>
                          <option value="signature">Signature</option>
                          <option value="number">Number</option>
                          <option value="tel">Phone</option>
                        </select>
                      </div>
                      
                      <div className="property-row">
                        <label>Placeholder:</label>
                        <input
                          type="text"
                          placeholder="Enter placeholder text"
                          value={field.placeholder || ''}
                          onChange={(e) =>
                            updateFieldProperty(
                              activeSection.id,
                              field.name,
                              'placeholder',
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="property-row">
                        <label>Default Value:</label>
                        <input
                          type="text"
                          placeholder="Default value (optional)"
                          value={field.defaultValue || ''}
                          onChange={(e) =>
                            updateFieldProperty(
                              activeSection.id,
                              field.name,
                              'defaultValue',
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-fields">No fields in this section</p>
              )}

              <button 
                className="add-field-btn"
                onClick={() => addCustomField(activeSection.id)}
              >
                ➕ Add Custom Field
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Debug Info */}
      <div className="debug-info">
        <small>
          Status: {loadingCompany ? 'Loading company...' : 
                  companyId ? `✅ Ready (ID: ${companyId.substring(0, 8)}...)` : 
                  '❌ No company ID'} | 
          Sections: {template.sections.length} | 
          Save Button: {companyId ? 'Enabled' : 'Disabled'}
        </small>
      </div>
    </div>
  );
};

export default TemplateBuilderPage;